/**
 * ★ Fase 59.P2 — estratto da LeadDetailPage.jsx (split componenti oversize).
 * Comportamento e data-testid INVARIATI.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UserCog, UserX } from 'lucide-react';

// ─── Assignment Card ─────────────────────────────────────────────────────
// Permette di cambiare l'Associate Manager e il Senior assegnati al lead.
// 2026-06-19 (richiesta utente): l'Associate corrente del lead può cambiare
// il Senior (es. dopo aver fissato una Zoom). Solo gli admin possono
// cambiare l'Associate stesso.
//
// • Carica la lista degli agenti via /api/leads/_assignable-agents
// • Su change → POST /api/leads/{id}/assign
// • Bottone "Rimuovi" per disassociare un agente (senza assegnare un nuovo)
// ─────────────────────────────────────────────────────────────────────────
export function AssignmentCard({ lead, isAdmin, currentUser, onUpdated }) {
  const { t } = useTranslation();
  const cf = lead.custom_fields || {};
  const [associateOpts, setAssociateOpts] = React.useState([]);
  const [seniorOpts, setSeniorOpts] = React.useState([]);
  const [seniorRestricted, setSeniorRestricted] = React.useState(false);
  const [associateRestricted, setAssociateRestricted] = React.useState(false);
  const [optsLoading, setOptsLoading] = React.useState(false);
  const [savingRole, setSavingRole] = React.useState(null); // 'associate' | 'senior' | null

  // ★ Permission (2026-07-14 richiesta utente):
  //   - admin / amministrativo: possono cambiare Associate + Senior
  //   - Associate corrente del lead: può cambiare Senior
  //   - Associate Manager (anche NON current associate del lead): può
  //     AUTO-ASSEGNARSI come Associate del lead (self-election forzata).
  //
  // La proprietà "Associate" di un lead può essere salvata in 2 punti diversi
  // per ragioni storiche: `assigned_agent_id` (top-level) o
  // `custom_fields.assigned_associate_id`. Accettiamo entrambi.
  const isCurrentAssociate = (
    !!currentUser?.agent_id
    && (
      cf.assigned_associate_id === currentUser.agent_id
      || lead.assigned_agent_id === currentUser.agent_id
    )
  );
  const isAssociateManagerUser = !!currentUser?.is_associate_manager;
  const canEditSenior = isAdmin || isCurrentAssociate;
  // ★ Associate Manager può auto-eleggersi anche se non è l'associate attuale
  const canEditAssociate = isAdmin || isAssociateManagerUser;

  // Carica le opzioni — admin: entrambe; non-admin: solo il self o senior
  React.useEffect(() => {
    if (!canEditSenior && !canEditAssociate) return;
    let cancelled = false;
    (async () => {
      setOptsLoading(true);
      try {
        const calls = [];
        if (canEditAssociate) {
          calls.push(api.get('/leads/_assignable-agents', { params: { role: 'associate' } }));
        } else {
          calls.push(Promise.resolve({ data: { agents: [], restricted: false } }));
        }
        if (canEditSenior) {
          calls.push(api.get('/leads/_assignable-agents', { params: { role: 'senior' } }));
        } else {
          calls.push(Promise.resolve({ data: { agents: [], restricted: false } }));
        }
        const [a, s] = await Promise.all(calls);
        if (cancelled) return;
        setAssociateOpts(a.data.agents || []);
        setAssociateRestricted(a.data.restricted === true);
        setSeniorOpts(s.data.agents || []);
        setSeniorRestricted(s.data.restricted === true);
      } catch (err) {
        logger.error('Failed to load assignable agents:', err);
        toast.error(t('assignment.loadAgentsError', 'Impossibile caricare la lista agenti'));
      } finally {
        if (!cancelled) setOptsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canEditSenior, canEditAssociate]);

  const handleAssign = async (role, agentId) => {
    setSavingRole(role);
    try {
      await api.post(`/leads/${lead.id}/assign`, {
        role,
        agent_id: agentId || null,
      });
      const roleLabel = role === 'associate' ? 'Associate' : 'Senior';
      toast.success(agentId ? `${roleLabel} ${t('assignment.updated', 'aggiornato')}` : `${roleLabel} ${t('assignment.removed', 'rimosso')}`);
      onUpdated?.();
    } catch (err) {
      logger.error('reassign error:', err);
      toast.error(err?.response?.data?.detail || t('assignment.reassignError', 'Errore durante la riassegnazione'));
    } finally {
      setSavingRole(null);
    }
  };

  const associateId = cf.assigned_associate_id || '';
  const associateName = cf.assigned_associate_name || null;
  const seniorId = cf.senior_agent_id || '';
  const seniorName = cf.senior_agent_name || null;

  return (
    <div data-testid="lead-assignment-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <UserCog className="w-3 h-3" /> {t('assignment.title', 'Assegnazioni agenti')}
        </div>
        {!canEditSenior && (
          <span className="text-[10px] text-muted-foreground italic">{t('assignment.readOnly', 'solo lettura')}</span>
        )}
        {!isAdmin && isCurrentAssociate && (
          <span className="text-[10px] text-emerald-700 italic">
            {t('assignment.canChangeSenior', 'puoi cambiare il Senior')}
          </span>
        )}
      </div>

      {/* ── Associate row ──────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-emerald-700">📞 Associate</span>
            {associateName && isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleAssign('associate', null)}
                disabled={savingRole === 'associate'}
                data-testid="lead-assoc-clear-button"
              >
                <UserX className="w-3 h-3 mr-1" /> Rimuovi
              </Button>
            )}
          </div>
          {canEditAssociate ? (
            <>
              {associateRestricted && !isAdmin && (
                <div
                  className="mb-1.5 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1 text-[10px] text-emerald-900"
                  data-testid="associate-restricted-banner"
                >
                  👤 {t('assignment.associateRestricted', 'Puoi solo auto-assegnarti come Associate di questo lead (i cambi tra Associate diversi restano riservati agli admin).')}
                </div>
              )}
              <Select
                value={associateId || 'none'}
                onValueChange={(v) => handleAssign('associate', v === 'none' ? null : v)}
                disabled={optsLoading || savingRole === 'associate'}
              >
                <SelectTrigger data-testid="lead-assoc-select">
                  <SelectValue placeholder={optsLoading ? t('common.loading', 'Caricamento…') : t('assignment.selectAssociate', '— Seleziona Associate —')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('manualProfile.none', '— Nessuno —')}</SelectItem>
                  {associateOpts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} · {a.role_label || a.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          ) : (
            <div className="text-sm py-1.5 px-3 rounded-md bg-muted/40 border border-border" data-testid="lead-assoc-readonly">
              {associateName || <span className="text-muted-foreground italic">— nessuno —</span>}
            </div>
          )}
        </div>

        {/* ── Senior row ────────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-amber-700">🎯 Senior</span>
            {seniorName && isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleAssign('senior', null)}
                disabled={savingRole === 'senior'}
                data-testid="lead-senior-clear-button"
              >
                <UserX className="w-3 h-3 mr-1" /> Rimuovi
              </Button>
            )}
          </div>
          {canEditSenior ? (
            <>
              {seniorRestricted && (
                <div
                  className="mb-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[10px] text-amber-900"
                  data-testid="senior-restricted-banner"
                >
                  🇦🇱 Lista ristretta: come Associate speciale Albania puoi scegliere
                  solo tra Mattia o Padoan.
                </div>
              )}
              <Select
                value={seniorId || 'none'}
                onValueChange={(v) => handleAssign('senior', v === 'none' ? null : v)}
                disabled={optsLoading || savingRole === 'senior'}
              >
                <SelectTrigger data-testid="lead-senior-select">
                  <SelectValue placeholder={optsLoading ? t('common.loading', 'Caricamento…') : t('assignment.selectSenior', '— Seleziona Senior —')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('manualProfile.none', '— Nessuno —')}</SelectItem>
                  {seniorOpts.map((a) => {
                    const sc = Array.isArray(a.senior_countries) ? a.senior_countries.join(', ') : '';
                    return (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}{sc ? ` · ${sc}` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </>
          ) : (
            <div className="text-sm py-1.5 px-3 rounded-md bg-muted/40 border border-border" data-testid="lead-senior-readonly">
              {seniorName || <span className="text-muted-foreground italic">— nessuno —</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
